import { useEffect, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import track, { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import { format } from 'date-fns';
import { Grid } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import DocumentHeader from '@/components/DocumentHeader';
import { Footer } from '@/components/Footer';
import {
  ContentWrapper,
  Content,
  PaginationContainer,
} from '@/components/ContentHub/styled';
import { ArticleCard } from '@/components/ContentHub/components/ArticleCard';
import { ArticleHighlight } from '@/components/ContentHub/components/ArticleHighlight';
import {
  getLatestArticlesQueryKey,
  useLatestArticles,
  ITEMS_PER_PAGE,
} from '@/hooks/useLatestArticles';
import { ROUTE_MARKET_HUB } from '@/constants/routes';
import Skeleton from './components/Skeleton';
const Pagination = loadable(() => import('@/components/Pagination'));

const MobileHeader = loadable(
  () => import('../../components/ContentHub/components/MobileHeader')
);
const DesktopHeader = loadable(
  () => import('../../components/ContentHub/components/DesktopHeader')
);
const MarketHub = () => {
  const { trackEvent } = useTracking();

  const currentPage = useSelector<
    { location: { query?: { page?: string } } },
    number
  >((state) => {
    const pageQuery = state.location.query?.page;
    if (
      typeof pageQuery === 'undefined' ||
      pageQuery.match(/^[0-9]+$/) === null
    ) {
      return 1;
    }
    return Number(pageQuery);
  }, shallowEqual);

  useEffect(() => {
    trackEvent({
      type: 'track',
      subject: 'page',
      action: 'landed',
    });
  }, [trackEvent]);

  const getPaginatedRoute = useCallback((page: number) => {
    return {
      type: ROUTE_MARKET_HUB,
      payload: {
        query: {
          page: String(page),
        },
      },
    };
  }, []);

  const { isMobile, isTablet, isDesktop } = useUserAgent();
  const offset = (currentPage - 1) * ITEMS_PER_PAGE;
  const { data, isFetching } = useLatestArticles(
    getLatestArticlesQueryKey({ offset })
  );
  const [highlightArticle, ...remainingArticles] = data?.articles ?? [];
  const totalPages = data?.total ? Math.ceil(data.total / ITEMS_PER_PAGE) : 1;

  if (isFetching) {
    return (
      <ContentWrapper>
        <Content>
          {isMobile || isTablet ? <MobileHeader /> : <DesktopHeader />}
          <Skeleton />
        </Content>
      </ContentWrapper>
    );
  }

  return (
    <>
      <DocumentHeader
        title="Weekly Market News & Insights"
        description="Stay up to date with global market news and insights. Written by financial analysts we connect the dots on global events to help your investment research."
        allowCrawlers={SWS_ENV === 'prod'}
      >
        <link rel="canonical" href="https://simplywall.st/markets/insights" />
      </DocumentHeader>
      <ContentWrapper>
        <Content>
          {isMobile || isTablet ? <MobileHeader /> : <DesktopHeader />}
          {highlightArticle && isDesktop && (
            <ArticleHighlight article={highlightArticle} />
          )}
          <Grid
            templateColumns="1fr"
            templateColumnsLg="1fr 1fr 1fr 1fr"
            gap="20px"
            rowGapMd="80px"
            rowGapSm="40px"
          >
            {highlightArticle && (isMobile || isTablet) && (
              <ArticleCard
                key={0}
                index={0}
                slug={highlightArticle.slug}
                title={highlightArticle.title}
                excerpt={highlightArticle.excerpt}
                imageUrl={highlightArticle.coverURL}
                publishedDate={format(
                  new Date(highlightArticle.created),
                  'MMMM dd, yyyy'
                )}
                author={highlightArticle.author?.name}
                authorAvatarUrl={highlightArticle.author?.photoURL}
                authorSlug={highlightArticle.author?.slug}
              />
            )}
            {remainingArticles &&
              remainingArticles.map((article, index) => (
                <ArticleCard
                  key={index}
                  index={index}
                  slug={article.slug}
                  title={article.title}
                  excerpt={article.excerpt}
                  imageUrl={article.coverURL}
                  publishedDate={format(
                    new Date(article.created),
                    'MMMM dd, yyyy'
                  )}
                  author={article.author?.name}
                  authorAvatarUrl={article.author?.photoURL}
                  authorSlug={article.author?.slug}
                />
              ))}
          </Grid>
          <PaginationContainer>
            <Pagination
              styleType="light"
              currentPage={currentPage}
              totalPages={totalPages}
              actionLink={(page) => getPaginatedRoute(page)}
              loading={isFetching}
            />
          </PaginationContainer>
        </Content>
      </ContentWrapper>
      <Footer />
    </>
  );
};

export default track({
  type: 'track',
  page: 'MarketHub',
})(MarketHub);
